import React from 'react';
import { AxiosError } from 'axios';
import { Typography } from '@one-thd/sui-atomic-components';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { OnApproveData, PayPalButtonOnApprove } from '@paypal/paypal-js';
import { ON_CLOSE_MEATBALLS } from '../../../constants/custom-events';
import { IB2BPaymentCard } from '../../../models/b2b/payments';
import { IMAGES_ASSETS_URL } from '../../../constants/links';
import { Card } from '../../shared/card/card';
import { getLastModifiedDate } from '../../../utils/date';
import {
  IPaymentCardCommonData,
  TPaymentErrorResponse,
} from '../../../models/common/payments';
import {
  IMenuItem,
  MeatballsMenu,
} from '../../shared/meatballs-menu/meatballs-menu';
import { CardType } from '../../../constants/payments';
import { pushErrorEvent } from '../../../utils/analytics';
import {
  CHANGES_SAVED_MSG,
  DELETED_PAYPAL_ACCOUNT_MSG,
} from '../../../constants/response-success-messages';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import { usePayments } from '../../../hooks/use-payments';
import { getPaymentsErrorCode } from '../../../utils/payments';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';

interface IPaypalCardProps {
  card: IPaymentCardCommonData;
  isB2B: boolean;
  index: number;
  onActionSuccess: (paymentId: string, message: string, index?: number) => void;
  onActionFailure: (paymentId: string, message: string) => void;
  handlePaypalEdit: (
    paypalApproveData: OnApproveData,
    paymentCard: IB2BPaymentCard | undefined,
  ) => Promise<void>;
  handlePaypalError: (paypalAccountId?: string) => void;
}

export const PaypalCard = ({
  card,
  isB2B,
  index,
  onActionSuccess,
  onActionFailure,
  handlePaypalEdit,
  handlePaypalError,
}: IPaypalCardProps) => {
  const { paypalCards } = usePayments();
  const currentPaypalCard = (
    paypalCards?.response as Array<IB2BPaymentCard>
  )?.find((payment) => payment.paymentId === card.paymentId);

  const {
    onEditPaypal,
    onViewPurchases,
    onEditPaypalNickName,
    openPaypalDeleteModal,
  } = useMeatballMenuActions();

  const handleCloseMeatBalls = () => {
    const closeMeatballsCustomEvent = new Event(ON_CLOSE_MEATBALLS);
    document.dispatchEvent(closeMeatballsCustomEvent);
  };

  const editPaypalOnApprove: PayPalButtonOnApprove = (data: OnApproveData) => {
    return handlePaypalEdit(data, currentPaypalCard).then(() => {
      handleCloseMeatBalls();
    });
  };

  const handleOpenEditNickNameModal = () => {
    const onSuccess = () => {
      onActionSuccess(card.paypalAccountId || '', CHANGES_SAVED_MSG.message);
    };

    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paypalAccountId || '', ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'edit nickname paypal card',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    onEditPaypalNickName(card, currentPaypalCard, onSuccess, onFailure);
  };

  const handleDeletePaypal = () => {
    const onSuccess = () => {
      onActionSuccess(
        card.paypalAccountId || '',
        DELETED_PAYPAL_ACCOUNT_MSG.message,
        index,
      );
    };

    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paypalAccountId || '', ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'delete paypal card',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    openPaypalDeleteModal(card, onSuccess, onFailure);
  };

  const menuItems: IMenuItem[] = [
    {
      value: 'Edit',
      isHidden: (!card.actions?.edit && isB2B) || !isB2B,
      action: () => null,
      payPalBtn: (
        <PayPalButtons
          fundingSource="paypal"
          createOrder={onEditPaypal}
          onApprove={editPaypalOnApprove}
          onError={() => {
            handlePaypalError(card.paypalAccountId);
            handleCloseMeatBalls();
          }}
          onCancel={() => {
            handleCloseMeatBalls();
          }}
          style={{
            height: 36,
          }}
        />
      ),
    },
    {
      value: 'View Purchases',
      isHidden: (!card.actions?.viewPurchases && isB2B) || !isB2B,
      action: () => {
        onViewPurchases(card, CardType.PAYPAL_ACCOUNT);
      },
    },
    {
      value: 'Add/Edit Nickname',
      isHidden: (!card.actions?.edit && isB2B) || !isB2B,
      action: handleOpenEditNickNameModal,
    },
    {
      value: 'Delete',
      isHidden: !card.actions?.delete && isB2B,
      action: handleDeletePaypal,
    },
  ];

  return (
    <Card
      isDefault={false}
      isInactive={false}
      defaultLabel="Default Payment Method"
    >
      <div
        className={`sui-flex sui-justify-between sui-align-center ${
          card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'
        }`}
      >
        <h3
          data-testid="nickname"
          className="sui-font-display sui-text-3xl sui-mr-4 sui-truncate sui-text-primary"
        >
          {isB2B && card.cardNickName}
        </h3>
        <div>
          <MeatballsMenu label="Payment" items={menuItems} />
        </div>
      </div>
      <div className="sui-flex sui-mb-6 sui-items-center">
        <img
          alt="paypal logo"
          width={57}
          height="100%"
          className="sui-inline-block sui-mr-4"
          src={`${IMAGES_ASSETS_URL}/paypal-logo-v2.svg`}
        />
        <div className="sui-mr-4 sui-overflow-hidden">
          <Typography truncate variant="body-lg" color="subtle">
            {card.paypalAccountId}
          </Typography>
        </div>
      </div>
      {card.addedBy && (
        <div className="sui-overflow-hidden">
          <Typography truncate color="subtle">
            Added by <span data-qm-mask="">{card.addedBy}</span>
          </Typography>
        </div>
      )}
      {card.lastModifiedDate && (
        <Typography color="subtle">
          Last edited {getLastModifiedDate(card.lastModifiedDate)}
        </Typography>
      )}
    </Card>
  );
};

PaypalCard.displayName = 'PaypalCard';
